import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../components/AppIcon';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../../components/ui/Toast';
import {
  profileService,
  GENDER_OPTIONS,
  RELATIONSHIP_OPTIONS,
  LANGUAGE_OPTIONS,
  THEME_OPTIONS
} from '../../api/user';
import { chargesService } from '../../api/charges';
import { toast } from 'react-toastify';
import { format, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import { Calendar, ChevronLeft, ChevronRight, Camera, Upload, X, Trash2, RefreshCw } from 'lucide-react';

const sanitizeValue = (value) => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
  }

  if (Array.isArray(value)) {
    const sanitizedArray = value
      .map((item) => sanitizeValue(item))
      .filter((item) => item !== undefined);
    return sanitizedArray.length ? sanitizedArray : undefined;
  }

  if (typeof value === 'object') {
    const sanitizedObject = Object.entries(value).reduce((acc, [key, val]) => {
      const sanitizedVal = sanitizeValue(val);
      if (sanitizedVal !== undefined) {
        acc[key] = sanitizedVal;
      }
      return acc;
    }, {});

    return Object.keys(sanitizedObject).length ? sanitizedObject : undefined;
  }

  if (Number.isNaN(value)) {
    return undefined;
  }

  return value;
};

// Map API -> component state (flatten nested personalDetails/professionalDetails)
const mapApiToState = (api) => {
  const pd = api?.personalDetails || {};
  const prof = api?.professionalDetails || {};
  const prefs = api?.preferences || {};
  const notif = prefs?.notifications || {};

  return {
    id: api?.id || api?._id || api?.userId || '',
    doctorId: api?.doctorId || api?.id || api?._id || api?.userId || '',
    name: api?.name || '',
    mobile: api?.mobile || '',
    email: api?.email || '',
    consultationFees: api?.consultationFees ?? '',
    followUpFees: api?.followUpFees ?? '',
    followUpValidityDays: api?.followUpValidityDays ?? '',
    firstName: pd.firstName || '',
    lastName: pd.lastName || '',
    dateOfBirth: pd.dateOfBirth || '',
    gender: pd.gender || '',
    address: pd.address || '',
    city: pd.city || '',
    state: pd.state || '',
    country: pd.country || '',
    postalCode: pd.postalCode || '',
    emergencyContact: {
      name: pd.emergencyContact?.name || '',
      phone: pd.emergencyContact?.phone || '',
      relationship: pd.emergencyContact?.relationship || ''
    },
    specialization: prof.specialization || '',
    qualification: prof.qualification || '',
    experience: (prof.experience ?? '') === '' ? '' : String(prof.experience),
    licenseNumber: prof.licenseNumber || '',
    department: prof.department || '',
    designation: prof.designation || '',
    profilePicture: api?.profilePicture || null,
    preferences: {
      notifications: {
        email: notif.email ?? true,
        sms: notif.sms ?? true,
        push: notif.push ?? true,
      },
      theme: prefs.theme || 'light',
      language: prefs.language || 'en',
    }
  };
};

// Map component state -> API nested payload
const buildProfilePayload = (data) => {
  const numericExperience =
    data.experience === '' || data.experience === null || data.experience === undefined
      ? undefined
      : Number(data.experience);

  const payload = sanitizeValue({
    name: data.name,
    mobile: data.mobile,
    personalDetails: {
      firstName: data.firstName,
      lastName: data.lastName,
      dateOfBirth: data.dateOfBirth || undefined,
      gender: data.gender,
      address: data.address,
      city: data.city,
      state: data.state,
      country: data.country,
      postalCode: data.postalCode,
      emergencyContact: sanitizeValue({ ...data.emergencyContact }),
    },
    professionalDetails: {
      specialization: data.specialization,
      qualification: data.qualification,
      experience: Number.isNaN(numericExperience) ? undefined : numericExperience,
      licenseNumber: data.licenseNumber,
      department: data.department,
      designation: data.designation,
    },
    preferences: sanitizeValue({ ...data.preferences }),
  });

  return payload && typeof payload === 'object' ? payload : {};
};

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2
});

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return '—';
  }

  return currencyFormatter.format(numeric);
};

const formatValidity = (days) => {
  if (days === undefined || days === null || days === '') {
    return 'No follow-up validity';
  }

  const numeric = Number(days);
  if (Number.isNaN(numeric) || numeric <= 0) {
    return 'No follow-up validity';
  }

  return `${numeric} day${numeric === 1 ? '' : 's'}`;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(timestamp));
  } catch (error) {
    console.warn('Failed to format timestamp', error);
    return '';
  }
};

const resolveCurrency = (rawValue, formattedValue) => {
  if (formattedValue && typeof formattedValue === 'string') {
    return formattedValue;
  }

  return formatCurrency(rawValue);
};

const resolveFollowUpValidity = (followUpInfo, validityDays) => {
  if (followUpInfo?.validityText) {
    return followUpInfo.validityText;
  }

  return formatValidity(validityDays);
};

const resolveDoctorDisplayName = (doctor) => {
  if (!doctor) {
    return '';
  }

  if (doctor.name) {
    return doctor.name;
  }

  const parts = [doctor.firstName, doctor.lastName].filter(Boolean);
  if (parts.length) {
    return parts.join(' ');
  }

  return doctor.email || '';
};

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const mergeProfileData = (updated = {}, previous = {}) => {
  if (!isPlainObject(updated)) {
    return updated !== undefined ? updated : previous;
  }

  const result = { ...previous };

  Object.entries(updated).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    if (isPlainObject(value)) {
      result[key] = mergeProfileData(value, previous?.[key] || {});
      return;
    }

    if (Array.isArray(value)) {
      result[key] = value;
      return;
    }

    result[key] = value;
  });

  return result;
};

// Custom Date Picker Component
const CustomDatePicker = ({ value, onChange, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => value ? new Date(value) : new Date());
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  useEffect(() => {
    if (value && !Number.isNaN(new Date(value).getTime())) {
      setViewDate(new Date(value));
    }
  }, [value]);

  const toggleCalendar = (e) => {
    e.preventDefault();
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (date) => {
    onChange(format(date, 'yyyy-MM-dd'));
    setIsOpen(false);
  };

  const handleMonthChange = (amount) => {
    setViewDate(prev => addMonths(prev, amount));
  };

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: startDate, end: endDate });

  const weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  const displayValue = value ? format(new Date(value), 'EEEE, MMMM d, yyyy') : '';

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggleCalendar}
        className={`w-full border-2 rounded-lg px-4 py-2.5 text-md font-medium text-left bg-white flex items-center justify-between transition-all ${error ? 'border-red-300' : isOpen ? 'border-[#2D869C] ring-2 ring-[#9DC9D4]' : 'border-gray-200 hover:border-[#2D869C]'
          }`}
      >
        <div className="flex items-center gap-3">
          <span className={value ? "text-gray-900" : "text-gray-400"}>
            {value ? displayValue : "Select Date of Birth"}
          </span>
        </div>
        <Calendar className="w-5 h-5 text-gray-500" />
      </button>

      {error && <p className="text-red-600 text-sm mt-1 font-semibold">{error}</p>}

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
          <div
            className="fixed z-[9999] bg-white rounded-xl border-2 border-gray-200 shadow-2xl w-[320px]"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] p-4 flex items-center justify-between text-white rounded-t-xl">
              <button type="button" onClick={(e) => { e.stopPropagation(); handleMonthChange(-1); }} className="p-1 hover:bg-white/20 rounded transition-colors">
                <ChevronLeft />
              </button>
              <span className="font-bold text-lg font-sans">{format(viewDate, 'MMMM yyyy')}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); handleMonthChange(1); }} className="p-1 hover:bg-white/20 rounded transition-colors">
                <ChevronRight />
              </button>
            </div>

            <div className="p-4 bg-white rounded-b-xl">
              <div className="grid grid-cols-7 mb-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wide">
                {weekDays.map(d => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {days.map(day => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const isSelected = value === dateStr;
                  const isTodayDate = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                  const isCurrentMonth = format(day, 'M') === format(viewDate, 'M');
                  const maxDate = format(new Date(), 'yyyy-MM-dd');
                  const isDisabled = dateStr > maxDate;

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); !isDisabled && handleSelect(day); }}
                      disabled={isDisabled}
                      style={{ color: isSelected ? '#fff' : !isCurrentMonth ? '#9ca3af' : '#111827' }}
                      className={`h-9 w-9 rounded-lg text-sm font-bold flex items-center justify-center transition-all mx-auto
                             ${isDisabled ? 'bg-gray-50 cursor-not-allowed opacity-40' : 'hover:bg-gray-100 cursor-pointer'}
                             ${isSelected ? 'bg-[#2D869C] shadow-md' : ''}
                             ${isTodayDate && !isSelected ? 'bg-yellow-100 border-2 border-yellow-300' : ''}
                           `}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between">
                <button
                  type="button"
                  onClick={() => { onChange(''); setIsOpen(false); }}
                  className="text-sm font-bold text-gray-500 hover:text-red-500 transition-colors uppercase tracking-wide"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => { handleSelect(new Date()); }}
                  className="text-sm font-bold text-[#2D869C] hover:text-[#236b7a] transition-colors uppercase tracking-wide"
                >
                  Today
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Profile = () => {
  const { toasts, removeToast, showSuccess, showError, showWarning, showInfo, showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [profileData, setProfileData] = useState({
    id: '',
    doctorId: '',
    name: '',
    mobile: '',
    consultationFees: '',
    followUpFees: '',
    followUpValidityDays: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    specialization: '',
    qualification: '',
    experience: '',
    licenseNumber: '',
    department: '',
    designation: '',
    profilePicture: null,
    preferences: {
      notifications: {
        email: true,
        sms: true,
        push: true
      },
      theme: 'light',
      language: 'en'
    }
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // ── Profile picture state ──
  const [showPictureModal, setShowPictureModal] = useState(false);
  const [pictureModalTab, setPictureModalTab] = useState('upload'); // 'upload' | 'webcam'
  const [pickerFile, setPickerFile] = useState(null);
  const [pickerPreview, setPickerPreview] = useState(null);
  const [webcamStream, setWebcamStream] = useState(null);
  const [webcamCaptured, setWebcamCaptured] = useState(null); // { blob, url }
  const [pictureUploading, setPictureUploading] = useState(false);
  const [pictureDeleting, setPictureDeleting] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const [currentDoctorId, setCurrentDoctorId] = useState('');
  const [charges, setCharges] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [chargeSubmitting, setChargeSubmitting] = useState(false);
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [chargesSummary, setChargesSummary] = useState({ doctor: null, total: 0 });
  const [chargeDeletingId, setChargeDeletingId] = useState(null);

  const createInitialChargeForm = () => ({
    id: null,
    serviceName: '',
    fees: '',
    followUpFees: '',
    validityDays: '',
    description: '',
    isActive: true
  });

  const [chargeForm, setChargeForm] = useState(() => createInitialChargeForm());

  const resetChargeForm = () => {
    setChargeForm(createInitialChargeForm());
  };

  const handleChargeInputChange = (field, value) => {
    setChargeForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const startAddCharge = () => {
    resetChargeForm();
    setShowChargeForm(true);
  };

  const startEditCharge = (charge) => {
    if (!charge) {
      return;
    }

    setChargeForm({
      id: charge._id || charge.id || null,
      serviceName: charge.serviceName || '',
      fees:
        charge.fees === 0 || charge.fees
          ? String(charge.fees)
          : '',
      followUpFees:
        charge.followUpFees === 0 || charge.followUpFees
          ? String(charge.followUpFees)
          : charge.followUpInfo?.fees === 0 || charge.followUpInfo?.fees
            ? String(charge.followUpInfo.fees)
            : '',
      validityDays:
        charge.validityDays === 0 || charge.validityDays
          ? String(charge.validityDays)
          : charge.followUpInfo?.validityDays === 0 || charge.followUpInfo?.validityDays
            ? String(charge.followUpInfo.validityDays)
            : '',
      description: charge.description || '',
      isActive: charge.isActive !== undefined ? charge.isActive : true
    });
    setShowChargeForm(true);
  };

  const handleChargeCancel = () => {
    resetChargeForm();
    setShowChargeForm(false);
    setChargeSubmitting(false);
  };

  // Optimistic state helpers for real-time feel
  const upsertChargeOptimistic = (partialCharge) => {
    const id = partialCharge._id || partialCharge.id;
    if (!id) return;
    setCharges((prev) => {
      const idx = prev.findIndex((c) => (c._id || c.id) === id);
      if (idx === -1) {
        return [partialCharge, ...prev];
      }
      const next = [...prev];
      next[idx] = { ...next[idx], ...partialCharge };
      return next;
    });
  };

  const fetchDoctorCharges = async (doctorId, { forceRefresh = false, silent = false } = {}) => {
    if (!doctorId) {
      return;
    }
    if (!silent) {
      setChargesLoading(true);
    }
    try {
      const params = forceRefresh ? { _cb: Date.now() } : {};
      const doctorCharges = await chargesService.getByDoctor(doctorId, params);

      let resolvedCharges = [];
      let doctorInfo = null;
      let totalRecords = 0;

      const assignFromPayload = (payload) => {
        if (!payload) {
          return;
        }

        if (Array.isArray(payload)) {
          resolvedCharges = payload;
          totalRecords = payload.length;
          return;
        }

        if (typeof payload === 'object') {
          if (!doctorInfo && payload.doctor) {
            doctorInfo = payload.doctor;
          }

          if (Array.isArray(payload.charges)) {
            resolvedCharges = payload.charges;
            totalRecords = payload.total ?? payload.charges.length;
            return;
          }

          if (Array.isArray(payload.data?.charges)) {
            resolvedCharges = payload.data.charges;
            totalRecords = payload.data.total ?? payload.total ?? payload.data.charges.length;
            if (!doctorInfo && payload.data.doctor) {
              doctorInfo = payload.data.doctor;
            }
            return;
          }

          if (Array.isArray(payload.data)) {
            resolvedCharges = payload.data;
            totalRecords = payload.total ?? payload.data.length;
            return;
          }
        }
      };

      assignFromPayload(doctorCharges);

      if (!doctorInfo && Array.isArray(resolvedCharges) && resolvedCharges.length) {
        const firstCharge = resolvedCharges.find((item) => item?.doctor);
        if (firstCharge?.doctor) {
          doctorInfo = firstCharge.doctor;
        }
      }

      const numericTotal = typeof totalRecords === 'string' ? Number(totalRecords) : totalRecords;
      const safeTotal = Number.isFinite(numericTotal) ? numericTotal : resolvedCharges.length;

      setCharges(Array.isArray(resolvedCharges) ? [...resolvedCharges] : []);
      setChargesSummary((prev) => ({
        doctor: doctorInfo || prev.doctor,
        total: safeTotal
      }));
    } catch (error) {
      console.error('Error fetching charges:', error);
      toast.error('Unable to load charges at the moment');
      setChargesSummary({ doctor: null, total: 0 });
    } finally {
      if (!silent) {
        setChargesLoading(false);
      }
    }
  };

  const handleChargeSubmit = async (event) => {
    event.preventDefault();

    const doctorId = currentDoctorId || profileData.doctorId || profileData.id;
    if (!doctorId) {
      toast.error('Doctor identifier missing. Please reload the profile.');
      return;
    }

    const trimmedServiceName = chargeForm.serviceName.trim();
    if (!trimmedServiceName) {
      toast.error('Service name is required');
      return;
    }

    const feesValue = Number(chargeForm.fees);
    if (Number.isNaN(feesValue) || feesValue < 0) {
      toast.error('Enter a valid fee amount');
      return;
    }

    const followUpValue =
      chargeForm.followUpFees === '' ? undefined : Number(chargeForm.followUpFees);
    if (followUpValue !== undefined && (Number.isNaN(followUpValue) || followUpValue < 0)) {
      toast.error('Enter a valid follow-up fee amount');
      return;
    }

    const validityValue =
      followUpValue !== undefined
        ? Number(chargeForm.validityDays)
        : undefined;

    if (followUpValue !== undefined && (Number.isNaN(validityValue) || validityValue <= 0)) {
      toast.error('Set follow-up validity in days');
      return;
    }

    setChargeSubmitting(true);

    try {
      const payload = sanitizeValue({
        serviceName: trimmedServiceName,
        fees: feesValue,
        followUpFees: followUpValue,
        validityDays: validityValue,
        description: chargeForm.description,
        isActive: chargeForm.isActive
      });

      // Snapshot for rollback in case of error
      const prevCharges = [...charges];
      const prevSummary = { ...chargesSummary };

      // Optimistic UI update
      if (chargeForm.id) {
        const optimistic = {
          _id: chargeForm.id,
          id: chargeForm.id,
          serviceName: payload.serviceName,
          fees: payload.fees,
          followUpFees: payload.followUpFees,
          validityDays: payload.validityDays,
          description: payload.description,
          isActive: payload.isActive,
          updatedAt: new Date().toISOString(),
          _optimistic: true,
        };
        upsertChargeOptimistic(optimistic);
      } else {
        const tempId = `tmp-${Date.now()}`;
        const optimistic = {
          _id: tempId,
          id: tempId,
          serviceName: payload.serviceName,
          fees: payload.fees,
          followUpFees: payload.followUpFees,
          validityDays: payload.validityDays,
          description: payload.description,
          isActive: payload.isActive,
          createdAt: new Date().toISOString(),
          _optimistic: true,
        };
        upsertChargeOptimistic(optimistic);
        setChargesSummary((prev) => ({ ...prev, total: (Number(prev.total) || 0) + 1 }));
      }

      // Close form immediately for snappier UX (before network round-trip)
      resetChargeForm();
      setShowChargeForm(false);

      // Perform API call & patch optimistic entry with actual response
      let serverCharge;
      if (chargeForm.id) {
        serverCharge = await chargesService.update(chargeForm.id, payload);
        toast.success('Charge updated successfully');
      } else {
        serverCharge = await chargesService.create({ doctorId, ...payload });
        toast.success('Charge added successfully');
      }

      if (serverCharge) {
        const normalizedId = serverCharge._id || serverCharge.id;
        upsertChargeOptimistic({ ...serverCharge, _id: normalizedId, id: normalizedId, _optimistic: false });
      }

      // Silent reconciliation to get any computed/aggregated fields
      setTimeout(() => {
        fetchDoctorCharges(doctorId, { forceRefresh: true, silent: true });
      }, 300);
    } catch (error) {
      console.error('Error saving charge:', error);
      const message = error.response?.data?.message || 'Failed to save charge details';
      toast.error(message);
      // Rollback optimistic changes
      setCharges(prevCharges);
      setChargesSummary(prevSummary);
    } finally {
      setChargeSubmitting(false);
    }
  };

  const handleChargeDelete = async (charge) => {
    const chargeId = charge?._id || charge?.id;
    if (!chargeId) {
      return;
    }

    const confirmationMessage = `Delete ${charge?.serviceName || 'this charge'}? This cannot be undone.`;
    // eslint-disable-next-line no-alert
    const confirmFn = typeof window !== 'undefined' && typeof window.confirm === 'function'
      ? window.confirm
      : () => true;
    const confirmed = confirmFn(confirmationMessage);
    if (!confirmed) {
      return;
    }

    const doctorId = currentDoctorId || profileData.doctorId || profileData.id;
    setChargeDeletingId(chargeId);

    try {
      await chargesService.remove(chargeId);
      toast.success('Charge deleted successfully');

      if (chargeForm.id === chargeId) {
        resetChargeForm();
        setShowChargeForm(false);
      }

      if (doctorId) {
        await fetchDoctorCharges(doctorId, { forceRefresh: true });
      } else {
        let filtered = [];
        setCharges((prev) => {
          filtered = prev.filter((item) => (item._id || item.id) !== chargeId);
          return filtered;
        });
        setChargesSummary((prev) => ({
          doctor: prev.doctor,
          total: filtered.length
        }));
      }
    } catch (error) {
      console.error('Error deleting charge:', error);
      const message = error.response?.data?.message || 'Failed to delete charge';
      toast.error(message);
    } finally {
      setChargeDeletingId(null);
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';

    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 0 ? age : '';
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (!currentDoctorId) {
      setCharges([]);
      setChargesSummary({ doctor: null, total: 0 });
      return;
    }

    fetchDoctorCharges(currentDoctorId, { forceRefresh: true });
  }, [currentDoctorId]);

  // Lightweight polling to keep charges fresh while viewing the Professional tab
  useEffect(() => {
    if (activeTab !== 'professional' || !currentDoctorId) return undefined;
    const interval = setInterval(() => {
      fetchDoctorCharges(currentDoctorId, { forceRefresh: true });
    }, 30000); // 30s
    return () => clearInterval(interval);
  }, [activeTab, currentDoctorId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const result = await profileService.getProfile();
      if (result) {
        const mappedProfile = mapApiToState(result);
        setProfileData((prev) => ({ ...prev, ...mappedProfile }));
        const derivedDoctorId =
          mappedProfile.doctorId ||
          result?.doctorId ||
          result?.id ||
          result?._id ||
          result?.userId ||
          '';
        setCurrentDoctorId(derivedDoctorId);

        // Fetch consultation fees separately to ensure freshest values
        try {
          const fees = await profileService.getConsultationFees();
          if (fees) {
            const toStringOrBlank = (v) => (v === null || v === undefined ? '' : String(v));
            setProfileData((prev) => ({
              ...prev,
              consultationFees: toStringOrBlank(fees.consultationFees),
              followUpFees: toStringOrBlank(fees.followUpFees),
              followUpValidityDays: toStringOrBlank(fees.followUpValidityDays)
            }));
          }
        } catch (feeError) {
          console.warn('Failed to fetch consultation fees separately', feeError);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to fetch profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      const payload = buildProfilePayload(profileData);

      if (!Object.keys(payload).length) {
        toast.info('No changes detected to update');
        return;
      }

      // Explicitly exclude password fields from profile updates
      const { password, currentPassword, newPassword, confirmPassword, ...cleanPayload } = payload;

      const updatedProfile = await profileService.updateProfile(cleanPayload);
      if (updatedProfile) {
        setProfileData((prev) => mergeProfileData(updatedProfile, prev));
      }

      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleConsultationFeesSubmit = async (e) => {
    e.preventDefault();
    try {
      const toNumberOrUndefined = (v) => {
        if (v === '' || v === null || v === undefined) return undefined;
        const n = Number(v);
        return Number.isNaN(n) ? undefined : n;
      };

      const payload = {};
      const consultation = toNumberOrUndefined(profileData.consultationFees);
      const follow = toNumberOrUndefined(profileData.followUpFees);
      const validity = toNumberOrUndefined(profileData.followUpValidityDays);

      if (consultation !== undefined) payload.consultationFees = consultation;
      if (follow !== undefined) payload.followUpFees = follow;
      if (validity !== undefined) payload.followUpValidityDays = Math.trunc(validity);

      if (Object.keys(payload).length === 0) {
        toast.warning('Enter at least one field to update');
        return;
      }

      setSubmitLoading(true);
      const resp = await profileService.updateConsultationFees(payload);
      setProfileData((prev) => ({
        ...prev,
        consultationFees: (resp?.consultationFees ?? resp?.fees?.consultationFees ?? prev.consultationFees),
        followUpFees: (resp?.followUpFees ?? resp?.fees?.followUpFees ?? prev.followUpFees),
        followUpValidityDays: (resp?.followUpValidityDays ?? resp?.fees?.followUpValidityDays ?? prev.followUpValidityDays),
      }));
      toast.success('Consultation fees updated');

      // Re-fetch to ensure server normalization (if any) reflected
      try {
        const refreshed = await profileService.getConsultationFees();
        if (refreshed) {
          const toStringOrBlank = (v) => (v === null || v === undefined ? '' : String(v));
          setProfileData((prev) => ({
            ...prev,
            consultationFees: toStringOrBlank(refreshed.consultationFees),
            followUpFees: toStringOrBlank(refreshed.followUpFees),
            followUpValidityDays: toStringOrBlank(refreshed.followUpValidityDays)
          }));
        }
      } catch (refreshErr) {
        console.warn('Consultation fees refresh failed', refreshErr);
      }
    } catch (error) {
      console.error('Error updating consultation fees:', error);
      const msg = error?.response?.data?.message || 'Failed to update consultation fees';
      toast.error(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    console.log('🔐 Password submit triggered');
    console.log('📝 Password data:', {
      currentPassword: passwordData.currentPassword ? '***' : 'empty',
      newPassword: passwordData.newPassword ? '***' : 'empty',
      confirmPassword: passwordData.confirmPassword ? '***' : 'empty'
    });

    // Validation: Check if passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      console.log('❌ Passwords do not match');
      toast.error('New passwords and Confirm password do not match.');
      return;
    }

    // Validation: Check password length
    if (passwordData.newPassword.length < 8) {
      console.log('❌ Password too short');
      toast.error('Password must be at least 8 characters long for security.');
      return;
    }

    // Validation: Check if current password is provided
    if (!passwordData.currentPassword || passwordData.currentPassword.trim() === '') {
      console.log('❌ Current password missing');
      toast.error('Please enter your current password.');
      return;
    }

    console.log('✅ All validations passed, making API call...');
    setSubmitLoading(true);

    try {
      const response = await profileService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword
      });

      console.log('✅ API response:', response);

      // Show success message from API or default
      const successMessage = response?.message || 'Password changed successfully';
      toast.success(`✅ ${successMessage}`);

      // Clear the form on success
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('❌ Error changing password:', error);

      // Show specific error messages from API
      const errorMessage = error.response?.data?.message || error.message || 'Failed to change password';

      // Handle specific error cases with custom messages
      if (errorMessage.includes('Too many')) {
        toast.error(`⏳ ${errorMessage}`);
      } else if (errorMessage.includes('incorrect') || errorMessage.includes('Current password')) {
        toast.error('❌ Current password is incorrect. Please try again.');
      } else if (errorMessage.includes('same') || errorMessage.includes('different')) {
        toast.error('❌ New password must be different from your current password.');
      } else {
        toast.error(`❌ ${errorMessage}`);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    if (section === 'root') {
      setProfileData(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      setProfileData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }
  };

  const handleNestedInputChange = (section, subsection, field, value) => {
    setProfileData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  // ── Webcam helpers ──
  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 } });
      setWebcamStream(stream);
      // Attach stream to video element on next tick
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 50);
    } catch (err) {
      showError('Unable to access webcam. Please check permissions: ' + (err.message || ''));
    }
  };

  const stopWebcam = () => {
    if (webcamStream) {
      webcamStream.getTracks().forEach(t => t.stop());
      setWebcamStream(null);
    }
  };

  const captureFromWebcam = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        setWebcamCaptured({ blob, url: URL.createObjectURL(blob) });
        stopWebcam();
      }
    }, 'image/jpeg', 0.92);
  };

  const openPictureModal = () => {
    setShowPictureModal(true);
    setPictureModalTab('upload');
    setPickerFile(null);
    setPickerPreview(null);
    setWebcamCaptured(null);
    stopWebcam();
  };

  const closePictureModal = () => {
    setShowPictureModal(false);
    stopWebcam();
    setPickerFile(null);
    if (pickerPreview) URL.revokeObjectURL(pickerPreview);
    setPickerPreview(null);
    if (webcamCaptured?.url) URL.revokeObjectURL(webcamCaptured.url);
    setWebcamCaptured(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { showError('File size must be under 5MB'); return; }
    if (pickerPreview) URL.revokeObjectURL(pickerPreview);
    setPickerFile(file);
    setPickerPreview(URL.createObjectURL(file));
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showError('Please drop an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { showError('File size must be under 5MB'); return; }
    if (pickerPreview) URL.revokeObjectURL(pickerPreview);
    setPickerFile(file);
    setPickerPreview(URL.createObjectURL(file));
  };

  const handlePictureUpload = async (fileArg) => {
    const fileToUpload = fileArg instanceof Blob && !(fileArg instanceof File)
      ? new File([fileArg], 'webcam-capture.jpg', { type: 'image/jpeg' })
      : fileArg;
    setPictureUploading(true);
    try {
      const result = await profileService.uploadAvatar(fileToUpload);
      const newPicture = result?.profilePicture || (result?.url ? result : null);
      setProfileData(prev => ({ ...prev, profilePicture: newPicture }));
      closePictureModal();
      showSuccess('Profile picture updated successfully!');
    } catch (err) {
      showError(err.message || 'Failed to upload profile picture');
    } finally {
      setPictureUploading(false);
    }
  };

  const handlePictureDelete = async () => {
    if (!profileData.profilePicture) return;
    setPictureDeleting(true);
    try {
      await profileService.deleteAvatar();
      setProfileData(prev => ({ ...prev, profilePicture: null }));
      showSuccess('Profile picture removed successfully');
    } catch (err) {
      showError(err.message || 'Failed to remove profile picture');
    } finally {
      setPictureDeleting(false);
    }
  };

  const isEditingCharge = Boolean(chargeForm.id);
  const hasCharges = Array.isArray(charges) && charges.length > 0;
  const isDoctorIdResolved = Boolean(currentDoctorId || profileData.doctorId || profileData.id);
  const doctorDisplayName = resolveDoctorDisplayName(chargesSummary.doctor);
  const displayedTotalCharges = Number.isFinite(chargesSummary.total)
    ? chargesSummary.total
    : Array.isArray(charges)
      ? charges.length
      : 0;

  const tabs = [
    { id: 'personal', label: 'Personal Information', icon: 'User' },
    { id: 'professional', label: 'Professional Details', icon: 'Briefcase' },
    { id: 'security', label: 'Security', icon: 'Shield' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D869C]"></div>
        <span className="ml-2 text-gray-600 font-medium">Loading profile...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ fontFamily: 'Poppins' }}>
      {/* Modern Hero Header */}
      <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-6">
              {/* Avatar — click to open picture modal */}
              <div className="relative group cursor-pointer" onClick={openPictureModal}>
                <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-4 border-white/30 shadow-xl overflow-hidden">
                  {profileData.profilePicture?.url ? (
                    <img
                      src={profileData.profilePicture.url}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={32} className="text-white" />
                  )}
                </div>
                {/* Camera overlay on hover */}
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera size={22} className="text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'sans-serif' }}>{profileData.name || profileData.email || 'Doctor Profile'}</h3>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Tab Navigation */}
      <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden">
        <nav className="flex overflow-x-auto border-b-2 border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-[140px] py-4 px-6 flex items-center justify-center gap-3 font-semibold text-xl whitespace-nowrap transition-all ${activeTab === tab.id
                  ? 'bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white'
                  : 'text-gray-600 hover:text-[#2D869C] hover:bg-gray-50'
                }`}
            >
              <Icon name={tab.icon} size={20} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        {/* Tab Content */}
        <div className="p-8">
          {activeTab === 'personal' && (
            <form onSubmit={handleProfileSubmit} className="space-y-8">
              {/* Basic Information Card */}
              <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-4 py-3">
                  <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Basic Information</h4>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={profileData.firstName}
                        onChange={(e) => handleInputChange('root', 'firstName', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={profileData.lastName}
                        onChange={(e) => handleInputChange('root', 'lastName', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="Enter last name"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Display Name
                      </label>
                      <input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => handleInputChange('root', 'name', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="Enter display name"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Mobile Number
                      </label>
                      <div className="relative">
                        <input
                          type="tel"
                          value={profileData.mobile}
                          onChange={(e) => handleInputChange('root', 'mobile', e.target.value)}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C] pl-10"
                          placeholder="Enter mobile number"
                        />
                        <Icon name="Phone" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Date of Birth
                      </label>
                      <CustomDatePicker
                        value={profileData.dateOfBirth ? profileData.dateOfBirth.split('T')[0] : ''}
                        onChange={(dateStr) => handleInputChange('root', 'dateOfBirth', dateStr ? new Date(dateStr).toISOString() : '')}
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Age
                      </label>
                      <input
                        type="text"
                        value={calculateAge(profileData.dateOfBirth) ? `${calculateAge(profileData.dateOfBirth)} years` : 'Not specified'}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium bg-gray-50 text-gray-500"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Gender
                      </label>
                      <select
                        value={profileData.gender}
                        onChange={(e) => handleInputChange('root', 'gender', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                      >
                        <option className="text-md font-medium" value="">Select Gender</option>
                        {GENDER_OPTIONS.map((option) => (
                          <option className="text-md font-medium" key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="">
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Email Address
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={profileData.email || ''}
                          readOnly
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium bg-gray-50 text-gray-500 pl-10"
                        />
                        <Icon name="Mail" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-4 py-3">
                  <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Address Information</h4>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Street Address
                      </label>
                      <textarea
                        rows={3}
                        value={profileData.address}
                        onChange={(e) => handleInputChange('root', 'address', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="Enter your full address"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        City
                      </label>
                      <input
                        type="text"
                        value={profileData.city}
                        onChange={(e) => handleInputChange('root', 'city', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        State
                      </label>
                      <input
                        type="text"
                        value={profileData.state}
                        onChange={(e) => handleInputChange('root', 'state', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="State"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Country
                      </label>
                      <input
                        type="text"
                        value={profileData.country}
                        onChange={(e) => handleInputChange('root', 'country', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="Country"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Postal Code
                      </label>
                      <input
                        type="text"
                        value={profileData.postalCode}
                        onChange={(e) => handleInputChange('root', 'postalCode', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="Postal code"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 py-3">
                  <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Emergency Contact</h4>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Contact Name
                      </label>
                      <input
                        type="text"
                        value={profileData.emergencyContact.name}
                        onChange={(e) => handleInputChange('emergencyContact', 'name', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-red-300 focus:border-red-600 transition-all bg-white hover:border-red-600"
                        placeholder="Emergency contact name"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Phone Number
                      </label>
                      <div className="relative">
                        <input
                          type="tel"
                          value={profileData.emergencyContact.phone}
                          onChange={(e) => handleInputChange('emergencyContact', 'phone', e.target.value)}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-red-300 focus:border-red-600 transition-all bg-white hover:border-red-600 pl-10"
                          placeholder="Phone number"
                        />
                        <Icon name="Phone" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        Relationship
                      </label>
                      <select
                        value={profileData.emergencyContact.relationship}
                        onChange={(e) => handleInputChange('emergencyContact', 'relationship', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-red-300 focus:border-red-600 transition-all bg-white hover:border-red-600"
                      >
                        <option className="text-md font-medium" value="">Select Relationship</option>
                        {RELATIONSHIP_OPTIONS.map((option) => (
                          <option className="text-md font-medium" key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t-2 border-gray-200 mt-6">
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {submitLoading && (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <Icon name="Save" size={22} />
                  <span className="text-xl font-bold" style={{ fontFamily: 'sans-serif' }}>Save Changes</span>
                </button>
              </div>
            </form>
          )}

          {activeTab === 'professional' && (
            <div className="space-y-8">
              <form onSubmit={handleProfileSubmit} className="space-y-8">
                {/* Professional Details Card */}
                <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-4 py-3">
                    <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Professional Details</h4>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                          Specialization
                        </label>
                        <input
                          type="text"
                          value={profileData.specialization}
                          onChange={(e) => handleInputChange('root', 'specialization', e.target.value)}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                          placeholder="e.g., Cardiology"
                        />
                      </div>
                      <div>
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                          Qualification
                        </label>
                        <input
                          type="text"
                          value={profileData.qualification}
                          onChange={(e) => handleInputChange('root', 'qualification', e.target.value)}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                          placeholder="e.g., MBBS, MD"
                        />
                      </div>
                      <div>
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                          Experience (Years)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={profileData.experience}
                          onChange={(e) => handleInputChange('root', 'experience', e.target.value === '' ? '' : e.target.value)}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                          placeholder="Years of experience"
                        />
                      </div>
                      <div>
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                          License Number
                        </label>
                        <input
                          type="text"
                          value={profileData.licenseNumber}
                          onChange={(e) => handleInputChange('root', 'licenseNumber', e.target.value)}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                          placeholder="e.g., MH/DOC/2015/12345"
                        />
                      </div>
                      <div>
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                          Department
                        </label>
                        <input
                          type="text"
                          value={profileData.department}
                          onChange={(e) => handleInputChange('root', 'department', e.target.value)}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                          placeholder="e.g., Cardiology Department"
                        />
                      </div>
                      <div>
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                          Designation
                        </label>
                        <input
                          type="text"
                          value={profileData.designation}
                          onChange={(e) => handleInputChange('root', 'designation', e.target.value)}
                          className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                          placeholder="e.g., Senior Cardiologist"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-6 border-t-2 border-gray-200 mt-6">
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {submitLoading && (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    )}
                    <Icon name="Save" size={22} />
                    <span className="text-lg font-bold" style={{ fontFamily: 'sans-serif' }}>Save Professional Details</span>
                  </button>
                </div>
              </form>

              {/* Doctor Consultation Fees (common for all consultations) */}
              <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 px-4 py-3 border-b-2 border-gray-200">
                  <h4 className="text-xl font-bold text-gray-700" style={{ fontFamily: 'sans-serif' }}>Default Consultation Fees</h4>
                  <p className="text-md text-gray-600 mt-1 font-medium">These apply to general consultations and follow-ups. You can add more specific services below.</p>
                </div>
                <div className="p-4">
                  <form onSubmit={handleConsultationFeesSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Consultation Fee</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={profileData.consultationFees}
                        onChange={(e) => handleInputChange('root', 'consultationFees', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="e.g., 500"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Follow-up Fee</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={profileData.followUpFees}
                        onChange={(e) => handleInputChange('root', 'followUpFees', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="e.g., 200"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Follow-up Validity (Days)</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={profileData.followUpValidityDays}
                        onChange={(e) => handleInputChange('root', 'followUpValidityDays', e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C]"
                        placeholder="e.g., 7"
                      />
                    </div>
                    <div className="md:col-span-3 flex justify-end pt-4 border-t-2 border-gray-200">
                      <button
                        type="submit"
                        disabled={submitLoading}
                        className="flex items-center justify-center rounded-xl gap-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white px-6 py-2.5 font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {submitLoading && (
                          <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                        )}
                        <Icon name="Save" size={22} />
                        <span className="text-lg font-bold" style={{ fontFamily: 'sans-serif' }}>Save Consultation Fees</span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl p-4 border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-gray-700" style={{ fontFamily: 'sans-serif' }}>Additional Services</h4>
                    <p className="text-md text-gray-600 font-medium">Manage service fees that reception and patients see while booking.</p>
                  </div>
                  <button
                    type="button"
                    onClick={startAddCharge}
                    disabled={!isDoctorIdResolved}
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    <Icon name="Plus" size={18} />
                    <span className="text-md font-bold" style={{ fontFamily: 'sans-serif' }}>Add Charge</span>
                  </button>
                </div>

                {!isDoctorIdResolved && (
                  <p className="text-sm text-[#CCB478] bg-[#CCB478]/10 rounded-md px-3 py-2 font-semibold">
                    We could not determine your doctor ID. Please refresh the page or contact the administrator before adding charges.
                  </p>
                )}

                {(doctorDisplayName || displayedTotalCharges) && (
                  <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-md font-medium text-gray-700">
                    <span>Total services: {displayedTotalCharges}</span>
                  </div>
                )}

                {showChargeForm && (
                  <form onSubmit={handleChargeSubmit} className="space-y-4 rounded-xl bg-gradient-to-br from-white to-[#9DC9D4]/5 shadow-sm p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Service Name</label>
                        <input
                          type="text"
                          value={chargeForm.serviceName}
                          onChange={(e) => handleChargeInputChange('serviceName', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., General Consultation"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Base Fee</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={chargeForm.fees}
                          onChange={(e) => handleChargeInputChange('fees', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., 500"
                        />
                      </div>
                      <div>
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Follow-up Fee (Optional)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={chargeForm.followUpFees}
                          onChange={(e) => handleChargeInputChange('followUpFees', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., 200"
                        />
                      </div>
                      <div>
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Follow-up Validity (Days)</label>
                        <input
                          type="number"
                          min="1"
                          value={chargeForm.validityDays}
                          onChange={(e) => handleChargeInputChange('validityDays', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., 7"
                          disabled={chargeForm.followUpFees === ''}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">Description</label>
                        <textarea
                          rows={3}
                          value={chargeForm.description}
                          onChange={(e) => handleChargeInputChange('description', e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-md focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Add helpful details for this service"
                        />
                      </div>
                      <div className="md:col-span-2 flex items-center">
                        <input
                          id="charge-active"
                          type="checkbox"
                          checked={chargeForm.isActive}
                          onChange={(e) => handleChargeInputChange('isActive', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="charge-active" className="ml-2 text-md font-semibold text-gray-600 uppercase tracking-wide">
                          Active for booking
                        </label>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={handleChargeCancel}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r text-gray-700 hover:from-[#4c4e4f] hover:to-[#4f5050] hover:text-white px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        disabled={chargeSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={chargeSubmitting}
                        className=" flex items-center justify-center gap-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {chargeSubmitting && (
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        )}
                        <Icon name="Save" size={18} />
                        <span>{isEditingCharge ? 'Update Charge' : 'Save Charge'}</span>
                      </button>
                    </div>
                  </form>
                )}

                <div className="space-y-3">
                  {chargesLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#2D869C] border-t-transparent"></div>
                      <span className="ml-2 text-sm text-[#838383] font-semibold">Loading charges...</span>
                    </div>
                  ) : hasCharges ? (
                    charges.map((charge) => {
                      // Force INR formatting regardless of any backend formatted strings
                      const baseFee = formatCurrency(charge.fees);
                      const followUpSource =
                        charge.followUpFees !== undefined && charge.followUpFees !== null && charge.followUpFees !== ''
                          ? charge.followUpFees
                          : charge.followUpInfo?.fees;
                      const hasFollowUpFee =
                        followUpSource !== undefined && followUpSource !== null && followUpSource !== '';
                      const followUpFee = hasFollowUpFee ? formatCurrency(followUpSource) : 'Not set';
                      const followUpValidity = hasFollowUpFee
                        ? resolveFollowUpValidity(charge.followUpInfo, charge.validityDays)
                        : 'No follow-up validity';

                      return (
                        <div
                          key={charge._id || charge.id || charge.serviceName}
                          className="flex flex-col gap-3 rounded-xl shadow-sm p-4 sm:flex-row sm:items-center sm:justify-between bg-white hover:shadow-md transition-all duration-200"
                        >
                          <div className="space-y-1">
                            <p className="text-lg font-semibold text-[#2D869C]">{charge.serviceName || 'Untitled Service'}</p>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-md text-[#838383]">
                              <span className="font-semibold text-gray-600">Base: {baseFee}</span>
                              <span className="font-semibold text-gray-600">Follow-up: {followUpFee}</span>
                              <span className=" font-semibold text-gray-600">{followUpValidity}</span>
                              <span className={charge.isActive ? 'text-[#2ac468] font-semibold' : 'text-[#E99797] font-semibold'}>
                                {charge.isActive ? 'Active' : 'Inactive'}
                              </span>
                              {/* Removed visual 'Saving…' badge for smoother UX */}
                            </div>
                            {charge.description && (
                              <p className="text-md font-medium text-[#838383]">{charge.description}</p>
                            )}
                            {(charge.updatedAt || charge.createdAt) && (
                              <p className="text-sm font-medium text-[#838383]">
                                Updated {formatTimestamp(charge.updatedAt || charge.createdAt)}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-3">
                            <button
                              type="button"
                              onClick={() => startEditCharge(charge)}
                        className=" flex items-center justify-center gap-2 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleChargeDelete(charge)}
                              disabled={chargeDeletingId === (charge._id || charge.id)}
                              className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#c04747] to-[#d45a5a] hover:from-[#a33a3a] hover:to-[#c04747] text-white px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                              {chargeDeletingId === (charge._id || charge.id) && (
                                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
                              )}
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="rounded-xl bg-[#9DC9D4]/10 p-6 text-center text-sm text-[#838383] font-semibold">
                      No charges configured yet. Click &ldquo;Add Charge&rdquo; to create one.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-8">
              {/* Security Warning */}
              <div className="bg-gradient-to-r from-[#2D869C]/20 to-[#3a9fb5]/10 border-2 border-[#2D869C]/40 rounded-xl p-5 flex items-start gap-4 shadow-sm">
                <Icon name="Shield" size={24} className="text-[#2D869C] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-md font-bold text-[#2D869C] mb-2" style={{ fontFamily: 'sans-serif' }}>Security Notice</p>
                  <p className="text-md text-gray-700 font-medium">Choose a strong password with at least 8 characters. Include uppercase, lowercase, numbers, and special characters.</p>
                </div>
              </div>

              {/* Password Change Card */}
              <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm">
                <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-4 py-3 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30">
                    <Icon name="Lock" size={24} className="text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white" style={{ fontFamily: 'sans-serif' }}>Change Password</h4>
                </div>
                <div className="p-4">
                <div className="space-y-6">
                  <div>
                    <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                      Current Password <span className="text-red-600">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.currentPassword ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C] pl-10 pr-10"
                        placeholder="Enter current password"
                        required
                      />
                      <Icon name="Lock" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('currentPassword')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#2D869C] transition-colors"
                      >
                        <Icon name={showPasswords.currentPassword ? "EyeOff" : "Eye"} size={18} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                      New Password <span className="text-red-600">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.newPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C] pl-10 pr-10"
                        placeholder="Enter new password"
                        minLength={8}
                        required
                      />
                      <Icon name="Key" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('newPassword')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#2D869C] transition-colors"
                      >
                        <Icon name={showPasswords.newPassword ? "EyeOff" : "Eye"} size={18} />
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 flex items-center gap-2 font-medium">
                      <Icon name="Info" size={14} />
                      Minimum 8 characters required
                    </p>
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                      Confirm New Password <span className="text-red-600">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirmPassword ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-medium focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] transition-all bg-white hover:border-[#2D869C] pl-10 pr-10"
                        placeholder="Confirm new password"
                        required
                      />
                      <Icon name="CheckCircle" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirmPassword')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#2D869C] transition-colors"
                      >
                        <Icon name={showPasswords.confirmPassword ? "EyeOff" : "Eye"} size={18} />
                      </button>
                    </div>
                  </div>
                </div>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t-2 border-gray-200 mt-6">
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {submitLoading && (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <Icon name="Shield" size={22} />
                  <span className="text-xl font-bold" style={{ fontFamily: 'sans-serif' }}>Update Password</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Toast Container for displaying notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* ── Profile Picture Modal ── */}
      {showPictureModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ fontFamily: 'Poppins' }}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closePictureModal} />

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Update Profile Picture</h3>
              <button onClick={closePictureModal} className="text-white/80 hover:text-white transition-colors">
                <X size={22} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b-2 border-gray-100">
              <button
                onClick={() => { setPictureModalTab('upload'); stopWebcam(); setWebcamCaptured(null); }}
                className={`flex-1 py-3 font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                  pictureModalTab === 'upload'
                    ? 'border-b-2 border-[#2D869C] text-[#2D869C]'
                    : 'text-gray-500 hover:text-[#2D869C]'
                }`}
              >
                <Upload size={16} /> Upload File
              </button>
              <button
                onClick={() => { setPictureModalTab('webcam'); setPickerFile(null); setPickerPreview(null); }}
                className={`flex-1 py-3 font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                  pictureModalTab === 'webcam'
                    ? 'border-b-2 border-[#2D869C] text-[#2D869C]'
                    : 'text-gray-500 hover:text-[#2D869C]'
                }`}
              >
                <Camera size={16} /> Use Webcam
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {/* ── Upload File Tab ── */}
              {pictureModalTab === 'upload' && (
                <div className="space-y-4">
                  {pickerPreview ? (
                    <div className="flex flex-col items-center gap-4">
                      <img src={pickerPreview} alt="Preview" className="w-40 h-40 rounded-full object-cover border-4 border-[#9DC9D4] shadow-lg" />
                      <p className="text-sm text-gray-500 font-medium">{pickerFile?.name}</p>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => { setPickerFile(null); if (pickerPreview) URL.revokeObjectURL(pickerPreview); setPickerPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                          className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-600 font-semibold hover:border-gray-400 transition-all text-sm"
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePictureUpload(pickerFile)}
                          disabled={pictureUploading}
                          className="px-6 py-2 rounded-lg bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white font-semibold hover:from-[#236b7a] hover:to-[#2D869C] transition-all text-sm disabled:opacity-60 flex items-center gap-2"
                        >
                          {pictureUploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Upload size={14} />}
                          {pictureUploading ? 'Uploading...' : 'Upload'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#2D869C] hover:bg-[#2D869C]/5 transition-all"
                      onDragOver={e => e.preventDefault()}
                      onDrop={handleFileDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={40} className="mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600 font-semibold mb-1">Drop an image here or click to browse</p>
                      <p className="text-gray-400 text-sm">JPEG, PNG, WebP — max 5MB</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                </div>
              )}

              {/* ── Webcam Tab ── */}
              {pictureModalTab === 'webcam' && (
                <div className="space-y-4 flex flex-col items-center">
                  {webcamCaptured ? (
                    /* Captured preview */
                    <div className="flex flex-col items-center gap-4">
                      <img src={webcamCaptured.url} alt="Captured" className="w-40 h-40 rounded-full object-cover border-4 border-[#9DC9D4] shadow-lg" />
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => { if (webcamCaptured?.url) URL.revokeObjectURL(webcamCaptured.url); setWebcamCaptured(null); startWebcam(); }}
                          className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-600 font-semibold hover:border-gray-400 transition-all text-sm flex items-center gap-2"
                        >
                          <RefreshCw size={14} /> Retake
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePictureUpload(webcamCaptured.blob)}
                          disabled={pictureUploading}
                          className="px-6 py-2 rounded-lg bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white font-semibold hover:from-[#236b7a] hover:to-[#2D869C] transition-all text-sm disabled:opacity-60 flex items-center gap-2"
                        >
                          {pictureUploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Upload size={14} />}
                          {pictureUploading ? 'Uploading...' : 'Use This Photo'}
                        </button>
                      </div>
                    </div>
                  ) : webcamStream ? (
                    /* Live webcam */
                    <div className="flex flex-col items-center gap-4 w-full">
                      <div className="rounded-xl overflow-hidden border-2 border-gray-200 bg-black aspect-square w-full max-w-xs mx-auto">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={captureFromWebcam}
                        className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white font-semibold hover:from-[#236b7a] hover:to-[#2D869C] transition-all flex items-center gap-2"
                      >
                        <Camera size={18} /> Capture Photo
                      </button>
                    </div>
                  ) : (
                    /* Start webcam prompt */
                    <div className="flex flex-col items-center gap-4 py-6">
                      <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                        <Camera size={36} className="text-gray-400" />
                      </div>
                      <p className="text-gray-500 font-medium text-sm text-center">Allow camera access to take a photo</p>
                      <button
                        type="button"
                        onClick={startWebcam}
                        className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white font-semibold hover:from-[#236b7a] hover:to-[#2D869C] transition-all flex items-center gap-2"
                      >
                        <Camera size={18} /> Start Camera
                      </button>
                    </div>
                  )}
                  {/* Hidden canvas for capture */}
                  <canvas ref={canvasRef} className="hidden" />
                </div>
              )}
            </div>

            {/* Footer — remove photo */}
            {profileData.profilePicture?.url && (
              <div className="px-6 pb-5 flex justify-center">
                <button
                  type="button"
                  onClick={handlePictureDelete}
                  disabled={pictureDeleting}
                  className="flex items-center gap-2 text-red-500 hover:text-red-700 font-semibold text-sm transition-colors disabled:opacity-60"
                >
                  {pictureDeleting ? <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" /> : <Trash2 size={15} />}
                  {pictureDeleting ? 'Removing...' : 'Remove Current Photo'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;